/**
 * SearchableSelect — custom dropdown with an in-list filter input.
 * Keyboard: Arrow keys navigate, Enter selects, Escape closes.
 */

import styles from './searchable-select.module.css';
import { useState, useRef, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  id?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  isLoading = false,
  disabled = false,
  id,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  const uid = useId();
  const inputId = id ?? uid;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const filtered =
    query.trim() === ''
      ? options
      : options.filter((o) =>
          o.label.toLowerCase().includes(query.toLowerCase()),
        );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Opening resets the highlight; adjusted during render for the same reason as
  // elsewhere — an effect would render once with the previous highlight and
  // then again to correct it, which is visible as a flicker on a long list.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) setHighlighted(0);
  }

  // Focusing is a side effect on the DOM, so it stays in an effect.
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlighted] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const selectOption = (opt: SelectOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlighted]) selectOption(filtered[highlighted]);
    }
  };

  return (
    <div
      ref={containerRef}
      className={[styles.container, disabled ? styles.disabled : null].filter(Boolean).join(' ')}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger button */}
      <button
        id={inputId}
        type="button"
        className={styles.trigger}
        onClick={() => { if (!disabled && !isLoading) setOpen((v) => !v); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || isLoading}
      >
        <span className={selectedLabel ? undefined : styles.placeholder}>
          {isLoading
            ? t('common.loading')
            : selectedLabel || placeholder || t('common.select')}
        </span>
        <span className={styles.chevron} aria-hidden>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={styles.dropdown} role="listbox">
          {/* Search box */}
          <div className={styles.searchWrap}>
            <input
              ref={searchRef}
              type="text"
              className={styles.search}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlighted(0); }}
              placeholder={searchPlaceholder ?? t('common.searchPlaceholder')}
              autoComplete="off"
            />
          </div>

          {/* Options */}
          <ul ref={listRef} className={styles.list}>
            {filtered.length === 0 ? (
              <li className={styles.noResults}>{t('common.noResults')}</li>
            ) : (
              filtered.map((opt, i) => (
                <li
                  key={opt.value}
                  className={[styles.option, opt.value === value ? styles.selected : null, i === highlighted ? styles.highlighted : null].filter(Boolean).join(' ')}
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseEnter={() => setHighlighted(i)}
                  onMouseDown={(e) => { e.preventDefault(); selectOption(opt); }}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
